import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { initials, statusMeta, byLastName, firstName } from "@/lib/format";
import { RefreshNow } from "@/components/RefreshNow";
import { Input } from "@/components/ui/input";
import { Search, UserPlus, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, ShieldCheck, Loader2, Cloud, UserCheck, Server, Eye, EyeOff } from "lucide-react";
import { SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { GuestInviteDialog } from "@/components/GuestInviteDialog";

const FILTERS = ["all", "active", "disabled", "offboarding", "leaved"];
const isSyncing = (u) => u._syncingUntil && new Date(u._syncingUntil) > new Date();

export default function Employees() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(FILTERS.includes(params.get("filter")) ? params.get("filter") : "all");
  const [dept, setDept] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [vis, setVis] = useState("visible");
  const savedSort = (() => { try { return JSON.parse(localStorage.getItem("sort:employees") || "null"); } catch { return null; } })();
  const [sort, setSort] = useState(savedSort?.key || "firstname");
  const [dir, setDir] = useState(savedSort?.dir || "asc");
  const cols = useColumns("employees2", ["title", "company", "firstname", "lastname", "city", "street", "zip", "description"]);
  const [loading, setLoading] = useState(true);
  const [hybrid, setHybrid] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [leaved, setLeaved] = useState([]);

  const loadUsers = (silent = false) => {
    if (!silent) setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  const toggleVis = async (uid, isVisible) => {
    setUsers((us) => us.map((x) => x.id === uid ? { ...x, isVisible } : x));
    try { await api.patch(`/users/${uid}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setUsers((us) => us.map((x) => x.id === uid ? { ...x, isVisible: !isVisible } : x)); }
  };
  useEffect(() => {
    loadUsers();
    api.get("/leaved-employees").then((r) => setLeaved(r.data)).catch(() => {});
    api.get("/config").then((r) => setHybrid(r.data.operationMode === "hybrid")).catch(() => {});
  }, []);

  useEffect(() => {
    const f = params.get("filter");
    if (f && FILTERS.includes(f)) setFilter(f);
  }, [params]);

  const anySyncing = useMemo(() => users.some(isSyncing), [users]);
  useEffect(() => {
    if (!anySyncing) return;
    const iv = setInterval(() => loadUsers(true), 6000);
    return () => clearInterval(iv);
  }, [anySyncing]);

  const departments = useMemo(() => Array.from(new Set(users.map((u) => u.department).filter(Boolean))).sort(), [users]);

  // Account type: Global Admin > Cloud-only (hybrid) > On-prem.
  const typeOf = (u) => u.isGlobalAdmin ? "globalAdmin" : (hybrid && u.isCloudOnly) ? "cloudOnly" : "onPrem";
  const TYPE_OPTS = [
    { value: "all", label: t("common.all") },
    { value: "globalAdmin", label: t("emp.type_globalAdmin") },
    ...(hybrid ? [{ value: "cloudOnly", label: t("emp.type_cloudOnly") }] : []),
    { value: "onPrem", label: t("emp.type_onPrem") },
  ];

  const isLeavedView = filter === "leaved";
  const filtered = useMemo(() => {
    const src = isLeavedView ? leaved : users;
    let r = src.filter((u) => {
      const okF = isLeavedView || filter === "all" || (u.status || (u.accountEnabled ? "active" : "disabled")) === filter;
      const okD = dept === "all" || u.department === dept;
      const okType = typeFilter === "all"
        || (typeFilter === "globalAdmin" && u.isGlobalAdmin)
        || (typeFilter === "cloudOnly" && u.isCloudOnly)
        || (typeFilter === "onPrem" && !u.isCloudOnly);
      const okVis = isLeavedView || vis === "all" || (vis === "visible" ? u.isVisible !== false : u.isVisible === false);
      const txt = `${u.displayName} ${u.userPrincipalName} ${u.jobTitle} ${u.department} ${u.companyName || ""} ${u.city || ""}`.toLowerCase();
      return okF && okD && okType && okVis && txt.includes(q.toLowerCase());
    });
    r = [...r].sort((a, b) => {
      let base;
      if (sort === "lastname") base = byLastName(a, b);
      else if (sort === "department") base = (a.department || "").localeCompare(b.department || "");
      else if (sort === "title") base = (a.jobTitle || "").localeCompare(b.jobTitle || "");
      else if (sort === "company") base = (a.companyName || "").localeCompare(b.companyName || "");
      else if (sort === "city") base = (a.city || "").localeCompare(b.city || "");
      else if (sort === "street") base = (a.streetAddress || "").localeCompare(b.streetAddress || "");
      else if (sort === "zip") base = (a.postalCode || "").localeCompare(b.postalCode || "");
      else if (sort === "description") base = (a.description || "").localeCompare(b.description || "");
      else if (sort === "type") base = typeOf(a).localeCompare(typeOf(b)) || (a.displayName || "").localeCompare(b.displayName || "");
      else if (sort === "visible") base = ((a.isVisible !== false) === (b.isVisible !== false)) ? 0 : (a.isVisible !== false ? 1 : -1);
      else if (sort === "status") {
        const st = (u) => u.status || (u.accountEnabled ? "active" : "disabled");
        base = st(a).localeCompare(st(b)) || (a.displayName || "").localeCompare(b.displayName || "");
      } else {
        base = firstName(a).toLowerCase().localeCompare(firstName(b).toLowerCase()) || (a.displayName || "").localeCompare(b.displayName || "");
      }
      return dir === "desc" ? -base : base;
    });
    return r;
  }, [users, leaved, isLeavedView, q, filter, dept, typeFilter, vis, sort, dir, hybrid]);
  const { visible, sentinelRef, hasMore, shown, total } = useInfiniteList(filtered, 50);
  useEffect(() => { localStorage.setItem("sort:employees", JSON.stringify({ key: sort, dir })); }, [sort, dir]);
  const sortObj = {
    sortKey: sort, sortDir: dir,
    toggle: (k) => { if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc")); else { setSort(k); setDir("asc"); } },
  };
  const nameActive = sort === "firstname" || sort === "lastname";
  const cycleName = () => {
    if (sort === "firstname" && dir === "asc") setDir("desc");
    else if (sort === "firstname") { setSort("lastname"); setDir("asc"); }
    else if (sort === "lastname" && dir === "asc") setDir("desc");
    else { setSort("firstname"); setDir("asc"); }
  };
  const empStatus = (u) => u.status || (u.accountEnabled ? "active" : "disabled");
  const typeLabel = (u) => t(`emp.type_${typeOf(u)}`);
  const COLUMN_DEFS = [
    { key: "type", label: t("emp.colType") },
    { key: "title", label: t("emp.colTitle") },
    { key: "department", label: t("common.department") },
    { key: "company", label: t("field.company") },
    { key: "firstname", label: t("field.givenName") },
    { key: "lastname", label: t("field.surname") },
    { key: "city", label: t("field.city") },
    { key: "street", label: t("field.street") },
    { key: "zip", label: t("field.zip") },
    { key: "description", label: t("field.description") },
    { key: "visible", label: t("det.visible") },
  ];
  const csvHeaders = [
    { key: "displayName", label: t("emp.colEmployee") },
    { key: "userPrincipalName", label: "UPN" },
    ...(cols.show("type") ? [{ key: "type", label: t("emp.colType") }] : []),
    ...(cols.show("title") ? [{ key: "jobTitle", label: t("emp.colTitle") }] : []),
    ...(cols.show("department") ? [{ key: "department", label: t("common.department") }] : []),
    ...(cols.show("company") ? [{ key: "companyName", label: t("field.company") }] : []),
    ...(cols.show("firstname") ? [{ key: "givenName", label: t("field.givenName") }] : []),
    ...(cols.show("lastname") ? [{ key: "surname", label: t("field.surname") }] : []),
    ...(cols.show("city") ? [{ key: "city", label: t("field.city") }] : []),
    ...(cols.show("street") ? [{ key: "streetAddress", label: t("field.street") }] : []),
    ...(cols.show("zip") ? [{ key: "postalCode", label: t("field.zip") }] : []),
    ...(cols.show("description") ? [{ key: "description", label: t("field.description") }] : []),
    { key: "status", label: t("common.status") },
  ];
  const csvRows = filtered.map((u) => ({
    displayName: u.displayName, userPrincipalName: u.userPrincipalName, type: typeLabel(u),
    jobTitle: u.jobTitle || "", department: u.department || "", companyName: u.companyName || "",
    givenName: u.givenName || "", surname: u.surname || "", city: u.city || "",
    streetAddress: u.streetAddress || "", postalCode: u.postalCode || "", description: u.description || "",
    status: isLeavedView ? "leaved" : empStatus(u),
  }));

  const TypeBadges = ({ u }) => {
    if (u.isGlobalAdmin || (hybrid && u.isCloudOnly)) {
      return (
        <div className="flex flex-wrap gap-1">
          {u.isGlobalAdmin && <span data-testid={`badge-ga-${u.id}`} title={t("det.gaProtectedTitle")} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700"><ShieldCheck size={10} /> {t("emp.gaBadge")}</span>}
          {hybrid && u.isCloudOnly && <span data-testid={`badge-cloud-${u.id}`} title={t("emp.cloudOnlyTitle")} className="inline-flex items-center gap-1 border border-sky-300 bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-700"><Cloud size={10} /> {t("emp.cloudOnlyBadge")}</span>}
        </div>
      );
    }
    return <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><Server size={11} className="text-zinc-400" /> {t("emp.type_onPrem")}</span>;
  };

  return (
    <>
      <PageHeader title={t("emp.title")} subtitle={`${users.length} ${t("emp.title").toLowerCase()}`}>
        <button onClick={() => setGuestOpen(true)} data-testid="employees-invite-guest-button" className="flex h-10 items-center gap-2 border border-zinc-300 bg-white px-4 text-sm font-semibold uppercase tracking-wide text-zinc-700 transition-colors hover:bg-zinc-50">
          <UserCheck size={16} /> {t("guest.invite")}
        </button>
        <Link to="/onboard" data-testid="employees-onboard-button" className="flex h-10 items-center gap-2 bg-primary px-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-[#002480]">
          <UserPlus size={16} /> {t("emp.onboard")}
        </Link>
      </PageHeader>

      <div className="p-6 md:p-10">
        <div className="mb-4 flex justify-end">
          <RefreshNow onDone={loadUsers} />
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("emp.searchPh")} data-testid="employees-search" className="rounded-sm border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={COLUMN_DEFS} />
            <ExportCsvButton label={t("cols.export")} filename="employees.csv" headers={csvHeaders} rows={csvRows} />
          </div>
        </div>

        <div className="hidden border border-zinc-200 bg-white overflow-x-auto md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <th className="px-4 py-3">
                  <button type="button" onClick={cycleName} data-testid="sort-name" className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider transition-colors hover:text-zinc-900 ${nameActive ? "text-zinc-900" : "text-inherit"}`}>
                    {t("emp.colEmployee")}
                    {nameActive ? (dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronsUpDown size={12} className="opacity-30" />}
                    <span className="text-[9px] font-normal normal-case text-zinc-400">({sort === "lastname" ? t("emp.sortLast") : t("emp.sortFirst")})</span>
                  </button>
                </th>
                {cols.show("type") && <SortTh label={t("emp.colType")} sortKey="type" sort={sortObj} filter={{ value: typeFilter, onChange: setTypeFilter, allValue: "all", options: TYPE_OPTS }} />}
                {cols.show("title") && <SortTh label={t("emp.colTitle")} sortKey="title" sort={sortObj} className="hidden md:table-cell" />}
                {cols.show("department") && <SortTh label={t("common.department")} sortKey="department" sort={sortObj} className="hidden lg:table-cell" filter={{ value: dept, onChange: setDept, allValue: "all", options: [{ value: "all", label: t("filter.allDepts") }, ...departments.map((d) => ({ value: d, label: d }))] }} />}
                {cols.show("company") && <SortTh label={t("field.company")} sortKey="company" sort={sortObj} className="hidden lg:table-cell" />}
                {cols.show("firstname") && <SortTh label={t("field.givenName")} sortKey="firstname" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("lastname") && <SortTh label={t("field.surname")} sortKey="lastname" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("city") && <SortTh label={t("field.city")} sortKey="city" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("street") && <SortTh label={t("field.street")} sortKey="street" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("zip") && <SortTh label={t("field.zip")} sortKey="zip" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("description") && <SortTh label={t("field.description")} sortKey="description" sort={sortObj} className="hidden xl:table-cell" />}
                {cols.show("visible") && !isLeavedView && <SortTh label={t("det.visible")} sortKey="visible" sort={sortObj} filter={{ value: vis, onChange: setVis, allValue: "all", options: [{ value: "visible", label: t("emp.visVisible") }, { value: "hidden", label: t("emp.visHidden") }, { value: "all", label: t("common.all") }] }} />}
                <SortTh label={t("common.status")} sortKey="status" sort={sortObj} filter={{ value: filter, onChange: setFilter, allValue: "all", options: FILTERS.map((f) => ({ value: f, label: f === "all" ? t("common.all") : statusMeta(f).label })) }} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={16} className="px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={16} className="px-4 py-10 text-center text-sm text-zinc-400">{t("emp.noMatch")}</td></tr>}
              {visible.map((u) => {
                const st = isLeavedView ? statusMeta("leaved") : statusMeta(u.status || (u.accountEnabled ? "active" : "disabled"));
                const detailHref = isLeavedView ? `/leaved/${u.id}` : `/employees/${u.id}`;
                return (
                  <tr key={u.id} onClick={() => navigate(detailHref)} className={`group cursor-pointer border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${u.isVisible === false ? "opacity-60" : ""}`} data-testid={`${isLeavedView ? "leaved" : "employee"}-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <Link to={detailHref} className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-zinc-900 text-xs font-bold text-white">{initials(u.displayName)}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-zinc-900">{u.displayName}</span>
                          <span className="block truncate font-mono text-xs text-zinc-400">{u.userPrincipalName}</span>
                        </span>
                      </Link>
                    </td>
                    {cols.show("type") && <td className="px-4 py-3" data-testid={`type-cell-${u.id}`}><TypeBadges u={u} /></td>}
                    {cols.show("title") && <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{u.jobTitle || "—"}</td>}
                    {cols.show("department") && <td className="hidden px-4 py-3 text-sm text-zinc-600 lg:table-cell">{u.department || "—"}</td>}
                    {cols.show("company") && <td className="hidden px-4 py-3 text-sm text-zinc-600 lg:table-cell">{u.companyName || "—"}</td>}
                    {cols.show("firstname") && <td className="hidden px-4 py-3 text-sm text-zinc-600 xl:table-cell">{u.givenName || "—"}</td>}
                    {cols.show("lastname") && <td className="hidden px-4 py-3 text-sm text-zinc-600 xl:table-cell">{u.surname || "—"}</td>}
                    {cols.show("city") && <td className="hidden px-4 py-3 text-sm text-zinc-600 xl:table-cell">{u.city || "—"}</td>}
                    {cols.show("street") && <td className="hidden px-4 py-3 text-sm text-zinc-600 xl:table-cell">{u.streetAddress || "—"}</td>}
                    {cols.show("zip") && <td className="hidden px-4 py-3 text-sm text-zinc-600 xl:table-cell">{u.postalCode || "—"}</td>}
                    {cols.show("description") && <td className="hidden max-w-[16rem] truncate px-4 py-3 text-sm text-zinc-600 xl:table-cell" title={u.description || ""}>{u.description || "—"}</td>}
                    {cols.show("visible") && !isLeavedView && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleVis(u.id, u.isVisible === false)} data-testid={`employee-vis-${u.id}`}
                        title={u.isVisible === false ? t("groups.toggleHidden") : t("groups.toggleVisible")}
                        className={`flex h-8 w-8 items-center justify-center border transition-colors ${u.isVisible === false ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                        {u.isVisible === false ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </td>}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                        {isLeavedView ? (
                          <span className="font-mono text-[10px] text-zinc-400">{u.offboardedOn || ""}</span>
                        ) : u.cloudSyncPending ? (
                          <span data-testid={`cloud-sync-pill-${u.id}`} title={t("sync.cloudPillTitle")} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                            <Cloud size={10} className="animate-pulse" /> {t("sync.cloudPill")}
                          </span>
                        ) : isSyncing(u) && (
                          <span data-testid={`sync-pill-${u.id}`} title={t("sync.pillTitle")} className="inline-flex items-center gap-1 border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-700">
                            <Loader2 size={10} className="animate-spin" /> {t("sync.pill")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right"><Link to={detailHref} data-testid={`manage-${u.id}`} className="inline-flex text-zinc-300 group-hover:text-primary"><ChevronRight size={18} /></Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="space-y-3 md:hidden" data-testid="employees-mobile-list">
          {loading && <div className="border border-zinc-200 bg-white px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div>}
          {!loading && filtered.length === 0 && <div className="border border-zinc-200 bg-white px-4 py-10 text-center text-sm text-zinc-400">{t("emp.noMatch")}</div>}
          {visible.map((u) => {
            const st = isLeavedView ? statusMeta("leaved") : statusMeta(u.status || (u.accountEnabled ? "active" : "disabled"));
            const detailHref = isLeavedView ? `/leaved/${u.id}` : `/employees/${u.id}`;
            return (
              <Link key={u.id} to={detailHref} data-testid={`${isLeavedView ? "leaved" : "employee"}-card-${u.id}`}
                className={`flex items-center gap-3 border border-zinc-200 bg-white p-4 ${u.isVisible === false ? "opacity-60" : ""}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-zinc-900 text-sm font-bold text-white">{initials(u.displayName)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-zinc-900">{u.displayName}</div>
                  <div className="truncate font-mono text-[11px] text-zinc-400">{u.userPrincipalName}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                    {u.jobTitle && <span className="text-[11px] text-zinc-500">{u.jobTitle}</span>}
                  </div>
                </div>
                <ChevronRight size={18} className="shrink-0 text-zinc-300" />
              </Link>
            );
          })}
        </div>

        {hasMore && <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-zinc-400"><Loader2 size={14} className="mr-2 animate-spin" /> {shown}/{total}</div>}
      </div>
      <GuestInviteDialog open={guestOpen} onOpenChange={setGuestOpen} />
    </>
  );
}
